import { EditTodoIcon } from '@/assets/icons/info-icons/editTodo';
import { RequiredIcon } from '@/assets/icons/required';
import { TierDropdown } from '@/components/dueDiligence-page/dueDiligenceRecord/tierDropdown';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { useResponsive } from '@/hooks/use-responsive';
import { useCustomizationsData } from '@/hooks/useCustomizationsData';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { getCacheBustedFileUrl } from '../../utils';
import { IFirmSettings, ISetting } from '../templatesSettings/types';
import { CatalogBannerText } from './catalogText';
import { CatalogBannerUploadLogo } from './uploadLogo';

interface IValuesMap {
  [key: string]: {
    value: any;
    id: string | null;
  };
}

const mapSettingsToFields = (keyArray: any, valueArray: ISetting[]) => {
  const valuesMap: IValuesMap = {};
  for (const item of valueArray) {
    valuesMap[item.key] = {
      value: JSON.parse(item.value),
      id: item.id
    };
  }

  const mappedItems = keyArray.map((item: any) => {
    const mappedValue = valuesMap[item.key] || { value: '', id: '' };
    return {
      fieldName: item.fieldName,
      value: mappedValue.value,
      key: item.key,
      id: mappedValue.id
    };
  });

  return mappedItems || [];
};

const getLogoSpecifications = (key: string): string[] => {
  const logoSpecifications: { [key: string]: string[] } = {
    bannerImageLogoUrl: ['1:1 Ratio', 'Solid Color', 'Transparent Background', 'SVG, WEBP'],
    bannerImageBackgroundUrl: ['3:1 Ratio (width:height) at least 1,500px wide', 'PNG, WEBP, SVG, JPEG, JPG']
  };

  return logoSpecifications[key] || [];
};

const imageWidths: { [key: string]: number } = {
  bannerImageLogoUrl: 60,
  bannerImageBackgroundUrl: 441
};

interface CatalogBannerProps {
  backToTitle: string;
  handleClose: () => void;
}

export const CatalogBanner = ({ backToTitle, handleClose }: CatalogBannerProps) => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { data: customizationsData } = useCustomizationsData();
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get('action') || '';
  const type = searchParams.get('type') || '';
  const windowType = searchParams.get('windowType') || 'default';
  const editRowKey = searchParams.get('edit') || '';

  const [catalogBannerImageData, setCatalogBannerImageData] = useState<IFirmSettings[]>([]);
  const [catalogTextData, setCatalogTextDataData] = useState<IFirmSettings[]>([]);
  const [currentRow, setCurrentRow] = useState<IFirmSettings | null>(null);
  const [scrollTo, setScrollTo] = useState(0);
  const [catalogBannerImageUrls, setCatalogBannerImageUrls] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const textSettings: any[] = [];
    const imageSettings: any[] = [];

    Object.entries(customizationsData?.theme?.catalogBanner).forEach(([key, value]) => {
      const entry = {
        key,
        value,
        fieldName: key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .replace(/url/i, '')
          .trim()
      };
      key.match(/url/i) ? imageSettings.push(entry) : textSettings.push(entry);
    });

    setCatalogTextDataData(textSettings);
    setCatalogBannerImageData(imageSettings);
    const imageUrls = imageSettings.reduce(
      (acc, item) => {
        acc[item.key] = item.value;
        return acc;
      },
      {} as { [key: string]: string }
    );

    setCatalogBannerImageUrls(imageUrls);
  }, [customizationsData]);

  useEffect(() => {
    if (!editRowKey) return;

    const row = catalogBannerImageData?.find((item) => item.key === editRowKey);
    setCurrentRow(row || null);
  }, [catalogBannerImageData]);

  useEffect(() => {
    if (windowType === 'default') {
      window.scrollTo(0, scrollTo);
    }
  }, [windowType]);

  const handleRedirect = (row: IFirmSettings, windowType: string) => {
    const queryParams = new URLSearchParams({
      action,
      ...(type && { type }),
      windowType,
      edit: row.key
    });

    navigate(`/settings?${queryParams.toString()}`);
    setScrollTo(window.scrollY);
    setCurrentRow(row);
  };

  const handleCloseEdit = () => {
    const queryParams = new URLSearchParams({
      action,
      ...(type && { type })
    });

    navigate(`/settings?${queryParams.toString()}`);
    setCurrentRow(null);
  };

  return (
    <>
      {windowType === 'edit-banner-image' && (
        <CatalogBannerUploadLogo currentRow={currentRow} catalogBannerImageUrls={catalogBannerImageUrls} handleClose={handleCloseEdit} />
      )}
      {windowType === 'edit-banner-text' && <CatalogBannerText currentRow={currentRow} handleClose={handleCloseEdit} />}
      {windowType === 'default' && (
        <>
          <MainWrap>
            <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle={backToTitle} />} />
            <PageTitle title="Catalog Banner" />
          </MainWrap>
          <MainWrap>
            <PaddingWrap>
              <SettingsContainer>
                <TierDropdown title="Banner Image">
                  {catalogBannerImageData?.map((item) => (
                    <RowWrapper key={item.fieldName} disabled={false} isMobile={isMobile}>
                      <InfoRowCell>{item.fieldName}</InfoRowCell>
                      <CatalogBannerImageWrapper>
                        {item.value ? (
                          <FieldValueWrapper>
                            {item.value && (
                              <ImageWrapper width={imageWidths[item.value]}>
                                <ImageItem src={getCacheBustedFileUrl(item.value)} alt={item.fieldName} />
                              </ImageWrapper>
                            )}
                          </FieldValueWrapper>
                        ) : (
                          <NoLogoText>No logo has been added.</NoLogoText>
                        )}
                        <LogoSpecifications>
                          Logo Specifications:
                          <LogoSpecificationsUnorderedList>
                            {getLogoSpecifications(item.key)?.map((specification) => <li key={specification}>{specification}</li>)}
                          </LogoSpecificationsUnorderedList>
                        </LogoSpecifications>
                      </CatalogBannerImageWrapper>
                      <EditTodoButton isMobile={isMobile} id="editTodoButton" onClick={() => handleRedirect(item, 'edit-banner-image')}>
                        <EditTodoIcon />
                      </EditTodoButton>
                    </RowWrapper>
                  ))}
                </TierDropdown>
                <TierDropdown title="Text">
                  {catalogTextData?.map((item) => (
                    <RowWrapper key={item.fieldName} disabled={false} isMobile={isMobile}>
                      <InfoRowCell>
                        {item.key === 'pageTitle' && <RequiredIcon />}
                        {item.fieldName}
                      </InfoRowCell>
                      {item.value ? <FieldValueWrapper>{item.value}</FieldValueWrapper> : <EmptyTodoRowCell>To Do</EmptyTodoRowCell>}
                      <EditTodoButton isMobile={isMobile} id="editTodoButton" onClick={() => handleRedirect(item, 'edit-banner-text')}>
                        <EditTodoIcon />
                      </EditTodoButton>
                    </RowWrapper>
                  ))}
                </TierDropdown>
              </SettingsContainer>
            </PaddingWrap>
          </MainWrap>
        </>
      )}
    </>
  );
};

const SettingsContainer = styled.div`
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CatalogBannerImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const NoLogoText = styled.p`
  padding: 10px 0;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: ${themeVar('font.weak')};
`;

const LogoSpecifications = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const LogoSpecificationsUnorderedList = styled.ul`
  margin-left: 25px;
  list-style-type: square;
`;

const EditTodoButton = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  margin-left: auto;
  cursor: pointer;
  height: 40px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '40px')};
  background-color: ${themeVar('action.default')};
  opacity: ${({ isMobile }) => (isMobile ? '1' : '0')};
`;

const ImageWrapper = styled.div<{ width: number }>`
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: ${({ width }) => `${width}px`};
  overflow: hidden;
  border-radius: 5px;
  padding: 10px;
  background-color: ${themeVar('action.active')};
`;

const ImageItem = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const RowWrapper = styled.div<{ disabled: boolean; isMobile: boolean }>`
  display: flex;
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'center')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '20px' : '0')};
  padding: 28px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.border.base};

  &:hover #editTodoButton {
    opacity: 1;
  }

  &:hover {
    background-color: ${({ disabled, theme }) => (disabled ? 'none' : theme.layer[2])};
    transition: background-color 100ms ease;
  }
`;

export const InfoRowCell = styled.div`
  max-width: 380px;
  width: 100%;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${({ theme }) => theme.font.strong};
  display: flex;
  gap: 10px;
  align-items: center;
`;

export const NoRequired = styled.div`
  width: 10px;
`;

export const EmptyTodoRowCell = styled.div`
  font-weight: 300;
  font-size: 16px;
  line-height: 22px;
  color: ${({ theme }) => theme.font.weak};
`;

export const FieldValueWrapper = styled.div`
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.font.base};
  max-width: 680px;
  width: 100%;
`;
